import React, {useContext, createContext, useState, useEffect, useMemo} from 'react';

import { IAuth } from '../@types';

import * as API from '../services/api';
import * as SSOAPI from '../services/ssoApi';
import { SSO } from "@rockr/sso";
import { TSSOLoginParams } from "../services/ssoApi";
import jwtDecode from "jwt-decode";

type AuthContext = {
  login: (data: any) => Promise<any>,
  ssoLogin: (params: TSSOLoginParams) => Promise<any>
  loadingSSOToken: boolean;
  auth: IAuth,
  token?: string;
  loading: boolean,
  error: string,
  userEmail?: string;
  logout: () => Promise<any>
  userName?: string;
}

const initContext: AuthContext = {
  login: () => null,
  ssoLogin: () => null,
  auth: null,
  loading: false,
  loadingSSOToken: true,
  error: null,
  logout: () => Promise.resolve()
};

export const AuthContext = createContext(initContext);

type AuthContextProviderProps = {
  children: React.ReactElement,
  ssoClient: SSO;
};

export const AuthContextProvider: React.FC<AuthContextProviderProps> = ({ children, ssoClient }) => {
  const [auth, setAuth] = useState<IAuth>(null);
  const [token, setToken] = useState<string>();
  const [loadingSSOToken, setLoadingSSOToken] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>(null);

  const userInfo = useMemo(() => {
    if (!token) return;
    return (jwtDecode(token) as any);
  }, [token])

  const userEmail = useMemo(() => userInfo?.email, [userInfo])
  const userName = useMemo(() => userInfo?.name, [userInfo])

  useEffect(() => {
    setLoadingSSOToken(true)
    ssoClient.getToken()
      .then((token) => setToken(token))
      .finally(() => setLoadingSSOToken(false))
  }, [])

  const handleLoginResponse = async (response: any) => {
    if (response.statusCode !== 200) {
      throw response;
    }

    await ssoClient.setToken(response.data.token)

    setToken(response.data.token)

    setError(null);
    setAuth(response?.data?.data);
    setLoading(false);
  }

  const logout = () => {
    return ssoClient
      .logout()
      .finally(() => setToken(undefined))
  }

  const login = async (data) => {
    setLoading(true);

    try {
      const response = await API.login(data);
      await handleLoginResponse(response)
      return response?.data?.statusCode;
    } catch (error) {
      setError(error)
      setLoading(false);
      return error?.statusCode;
    }
  }


  const ssoLogin = async (params: TSSOLoginParams) => {
    setLoading(true);

    try {
      const response = await SSOAPI.login(params)

      await handleLoginResponse(response)

      return response?.statusCode;

    } catch (error) {
      setError(error)
      setLoading(false);
      return error?.statusCode;
    }
  }

  return (
    <AuthContext.Provider value={{
      login,
      ssoLogin,
      auth,
      loading,
      token,
      error,
      loadingSSOToken,
      userEmail,
      userName,
      logout
    }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export default useAuth;
